import createClient, { type Middleware } from 'openapi-fetch';

import { currentToken, endSession } from '@/stores/session';

import { networkError, toApiError } from './errors';
import { DEV_API_BASE_URL } from './origin';
import type { paths } from './schema';

/**
 * Root of the API, *without* a version segment.
 *
 * The version is part of every generated path (`/v1/auth/login`), so appending
 * it here as well would produce `/api/v1/v1/...`.
 *
 * **The fallback is development-only, and that is the whole point.** Vite
 * substitutes `import.meta.env` at build time, so this line decides the origin
 * of a *built* bundle. It used to fall back to the local stack unconditionally,
 * which meant a production build with the variable unset shipped a site that
 * asked the visitor's own computer for the API: every screen loaded, every
 * request failed, and nothing said why. `vite build` now refuses to run without
 * `VITE_API_BASE_URL` (see `requireApiBaseUrl` in `vite.config.ts`), and an
 * empty string here is caught at boot by `ConfigurationGuard`. Two layers,
 * because the build guard cannot see a `dist/` that was built elsewhere.
 *
 * `npm run dev` outside Docker keeps the convenience it had.
 */
export const API_BASE_URL =
    import.meta.env.VITE_API_BASE_URL || (import.meta.env.DEV ? DEV_API_BASE_URL : '');

/** Attaches the bearer token to every authenticated request. */
const authenticate: Middleware = {
    onRequest({ request }) {
        const token = currentToken();

        if (token !== null) {
            request.headers.set('Authorization', `Bearer ${token}`);
        }

        request.headers.set('Accept', 'application/json');

        return request;
    },
};

/**
 * Ends the local session as soon as the API says the token is gone.
 *
 * Without this the app would keep firing requests with a dead token and show
 * error states instead of the login screen.
 */
const handleExpiredSession: Middleware = {
    onResponse({ response }) {
        if (response.status === 401 && currentToken() !== null) {
            endSession();
        }

        return response;
    },
};

const client = createClient<paths>({
    baseUrl: API_BASE_URL,
    // Resolved on every call instead of captured at module load.
    //
    // openapi-fetch defaults to `globalThis.fetch` bound at creation time. The
    // client module is imported while the test files are being collected, which
    // is before MSW installs its interceptor — so the captured reference stayed
    // the real fetch and the component tests silently hit the live API instead
    // of the mock. Looking the global up per request removes that ordering
    // dependency entirely.
    fetch: (request) => globalThis.fetch(request),
});

client.use(authenticate);
client.use(handleExpiredSession);

export { client };

/**
 * The shape openapi-fetch actually resolves to.
 *
 * The `| undefined` on each member is load-bearing under
 * `exactOptionalPropertyTypes`. Written as a bare `data?: TData`, the property
 * may be absent but may not be present-and-undefined — which is precisely what
 * openapi-fetch produces, so an endpoint whose response is a union (the weekly
 * plan, which is either a plan or null) failed to assign at all. The looser
 * declaration is the accurate one, not a concession.
 */
interface ApiCallResult<TData> {
    data?: TData | undefined;
    // `unknown` already admits undefined, so no explicit union is needed here.
    error?: unknown;
    response: Response;
}

/**
 * Turns an openapi-fetch result into a value or a thrown {@link ApiError}.
 *
 * TanStack Query expects promises that reject, and a single error type means
 * every caller handles failures the same way.
 */
export async function unwrap<TData>(call: Promise<ApiCallResult<TData>>): Promise<TData> {
    let result: ApiCallResult<TData>;

    try {
        result = await call;
    } catch {
        // fetch only rejects on transport failures, never on an HTTP status.
        throw networkError();
    }

    /*
     * The status decides, not the presence of a parsed error body.
     *
     * This read `result.error !== undefined`, and openapi-fetch does not always
     * fill that in: for a response carrying `Content-Length: 0` without chunked
     * encoding it returns `{ error: undefined }` rather than parsing a body it
     * has been told is not there (`dist/index.mjs`, the 204/HEAD/empty branch).
     * A proxy answering for a container that is not up does exactly that. The
     * old test then fell through to the `data === undefined` line below and
     * *resolved*, and what reached the reader was decided by whichever accident
     * caught it next — measured, both of them, not reasoned about:
     *
     * - a **query** did fail, but on TanStack Query's own refusal of an
     *   `undefined` result, so the rejection was a bare `Error` with no status
     *   on it. Every branch that reads one therefore read it wrong: a bodyless
     *   404 on a workout produced "De training kon niet worden geladen" — try
     *   again, then — where the honest answer is that the workout is not there.
     * - a **mutation** failed only because `onSuccess` dereferenced the
     *   `undefined` it had been handed, so the reader got "Er ging iets mis."
     *   out of a TypeError rather than anything about what the server said.
     *
     * Both cases have a test in `WorkoutDetailPage.test.tsx`; both fail if this
     * line goes back to reading `error`.
     */
    if (!result.response.ok) {
        throw toApiError(result.response.status, result.error);
    }

    if (result.data === undefined) {
        // 204 No Content, which callers type as void.
        return undefined as TData;
    }

    return result.data;
}
